import floydSteinberg from 'floyd-steinberg';
import Jimp from 'jimp/es';

import billAtkinsonDither from '~/main/printer/receipt/image-printing/bill-atkinson-dither';
import { PRINTER_BRANDS } from '~/main/printer/types';
import { LoggerScope } from '~/main/utils/logger';
import { defaultPrintSettings } from '~/renderer/store/hub/default';
import logger from '~/renderer/utils/logger';
import {
  getColorVariations,
  MAX_COLOR_VARIATIONS,
} from '~/renderer/utils/transform-html/color-variations';
import { removeBackground } from '~/renderer/utils/transform-html/remove-background';

import type { PrintSettings } from '~/renderer/store/hub/types';

interface DitherImageParams {
  image: string | Buffer;
  brand?: PRINTER_BRANDS;
  printSettings?: PrintSettings;
}

export const ditherAlgorithm = {
  [PRINTER_BRANDS.EPSON]: billAtkinsonDither,
  [PRINTER_BRANDS.LOGIC_CONTROLS]: billAtkinsonDither,
  [PRINTER_BRANDS.STAR]: floydSteinberg,
  [PRINTER_BRANDS.ZEBRA]: billAtkinsonDither,
};

export const ditherImage = async (params: DitherImageParams): Promise<string> => {
  const defaultSize = 500;
  try {
    const { image, brand, printSettings = defaultPrintSettings } = params;

    let { isDithered } = printSettings;
    let ditheredImg = await Jimp.read(image as string);
    ditheredImg.resize(defaultSize, Jimp.AUTO);

    let colorVariations = 0;
    if (printSettings.isCustomized) {
      if (printSettings.brightness < 0 && !ditheredImg.hasAlpha()) {
        ditheredImg = await removeBackground(ditheredImg);
      }

      // Adjust the brightness by a value -0.75 to +0.75
      ditheredImg.brightness(printSettings.brightness);
    }

    // Set white background to image with transparency
    if (ditheredImg.hasAlpha()) {
      ditheredImg.background(0xffffff);
      ditheredImg.colorType(0);
      // Update the image.bitmap data
      const buffer = await ditheredImg.getBufferAsync(ditheredImg.getMIME());
      ditheredImg = await Jimp.read(buffer);
    }

    if (!printSettings.isCustomized) {
      isDithered = false;
      colorVariations = getColorVariations(ditheredImg.bitmap).length;
    }

    if (brand && (isDithered || colorVariations >= MAX_COLOR_VARIATIONS)) {
      ditheredImg.resize(1000, Jimp.AUTO);
      // Don't dither images when they are very low on color variants for better results
      ditheredImg.bitmap = ditherAlgorithm[brand](ditheredImg.bitmap);
      ditheredImg.resize(defaultSize, Jimp.AUTO);
    }

    // If Jimp.AUTO is passed as the MIME type then the original MIME type for the image (or "image/png") will be used.
    const bas64str = (await ditheredImg.getBufferAsync(Jimp.AUTO as unknown as string)).toString(
      'base64',
    );
    return bas64str;
  } catch (err) {
    // The customer logo is invalid, no need to log this
    logger({ scope: LoggerScope.PRINTER }).debug({ err, params });
    return '';
  }
};
